import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Plus, Trash2, Edit, Check, X, Users, RefreshCw, Users2, Search, UserPlus, KeyRound, Mail } from 'lucide-react';
import { usersApi, schoolsApi, adminApi, parentLinksApi } from '../../lib/api';
import { toast } from 'sonner';
import { Textarea } from '../../components/ui/textarea';

export const UsersManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleData, setRoleData] = useState({ role: '', school_id: '', grade: '', board: '' });
  // Phase 40.35 — link parent inside the Edit User dialog for students
  const [linkedParent, setLinkedParent] = useState(null);
  const [parentSearch, setParentSearch] = useState('');
  const [parentSearchResults, setParentSearchResults] = useState([]);
  const [savingUser, setSavingUser] = useState(false);
  // Phase 40.35 — dev DB may not have any schools yet; always allow the
  // SuperAdmin to attach a school directly from the dialog via a Free-form
  // text input as a fallback. We also always show the parent-link section
  // for students regardless of `isSuper` so School Admins (who legitimately
  // manage same-school students) can attach a parent too.
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [deleting, setDeleting] = useState(false);

  // Phase 38.5 — admin parent ↔ child linking
  const [showChildrenDialog, setShowChildrenDialog] = useState(false);
  const [parentForChildren, setParentForChildren] = useState(null);
  const [linkedChildren, setLinkedChildren] = useState([]);
  const [childSearch, setChildSearch] = useState('');
  const [childSearchResults, setChildSearchResults] = useState([]);
  const [linkingChild, setLinkingChild] = useState(false);
  const [parentLinkCounts, setParentLinkCounts] = useState({});

  const refreshParentLinkCounts = async () => {
    try {
      const r = await parentLinksApi.counts();
      setParentLinkCounts(r.counts || {});
    } catch { /* ignore */ }
  };

  const openChildrenDialog = async (parentUser) => {
    setParentForChildren(parentUser);
    setShowChildrenDialog(true);
    setChildSearch('');
    setChildSearchResults([]);
    try {
      const r = await parentLinksApi.list({ parentId: parentUser.user_id });
      setLinkedChildren(r.links || []);
    } catch (e) {
      toast.error('Failed to load children');
      setLinkedChildren([]);
    }
  };

  const searchStudents = async (q) => {
    setChildSearch(q);
    if (q.trim().length < 2) { setChildSearchResults([]); return; }
    try {
      const r = await parentLinksApi.searchStudents(q.trim());
      const linkedIds = new Set(linkedChildren.map(c => c.child_id));
      setChildSearchResults((r.students || []).filter(s => !linkedIds.has(s.user_id)));
    } catch (e) {
      setChildSearchResults([]);
    }
  };

  const linkChild = async (student) => {
    setLinkingChild(true);
    try {
      await parentLinksApi.create({
        parentId: parentForChildren.user_id,
        childId: student.user_id,
      });
      toast.success(`Linked ${student.name} to ${parentForChildren.name}`);
      const r = await parentLinksApi.list({ parentId: parentForChildren.user_id });
      setLinkedChildren(r.links || []);
      setChildSearchResults(prev => prev.filter(s => s.user_id !== student.user_id));
      refreshParentLinkCounts();
    } catch (e) {
      toast.error(e.message || 'Failed to link');
    } finally {
      setLinkingChild(false);
    }
  };

  const unlinkChild = async (childId, name) => {
    if (!window.confirm(`Unlink ${name} from ${parentForChildren.name}?`)) return;
    try {
      await parentLinksApi.remove({
        parentId: parentForChildren.user_id, childId,
      });
      toast.success('Unlinked');
      setLinkedChildren(prev => prev.filter(c => c.child_id !== childId));
      refreshParentLinkCounts();
    } catch (e) {
      toast.error(e.message || 'Failed to unlink');
    }
  };

  // Phase 40.33 — Add | Delete | Reset password | Notify
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '', email: '', role: 'org_student', school_id: '', grade: '', board: ''
  });
  const [creating, setCreating] = useState(false);
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [notifyTarget, setNotifyTarget] = useState(null);
  const [notify, setNotify] = useState({ subject: '', message: '' });
  const [notifying, setNotifying] = useState(false);

  const isSuper = currentUser?.role === 'super_admin';
  const isSchoolAdmin = currentUser?.role === 'org_admin';

  const canManage = (u) => {
    if (!u) return false;
    if (u.user_id === currentUser?.user_id) return false;
    if (u.role === 'super_admin') return false;
    if (isSuper) return true;
    if (isSchoolAdmin) {
      if (['super_admin', 'org_admin'].includes(u.role)) return false;
      return !!currentUser?.school_id && u.school_id === currentUser.school_id;
    }
    return false;
  };

  const handleCreateUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim()) {
      return toast.error('Name and email are required');
    }
    setCreating(true);
    try {
      const payload = {
        name: newUser.name.trim(),
        email: newUser.email.trim().toLowerCase(),
        role: newUser.role,
        school_id: isSchoolAdmin ? currentUser?.school_id : (newUser.school_id || null),
        grade: newUser.grade ? parseInt(newUser.grade) : null,
        board: newUser.board || null,
      };
      const r = await usersApi.create(payload);
      toast.success(r.temp_password ? `User created. Temp password: ${r.temp_password}` : 'User created');
      setShowAddDialog(false);
      setNewUser({ name: '', email: '', role: 'org_student', school_id: '', grade: '', board: '' });
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})?  This cannot be undone.`)) return;
    try {
      await usersApi.remove(u.user_id);
      toast.success('User deleted');
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  const handleResetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.name}? All their active sessions will be signed out.`)) return;
    try {
      const r = await usersApi.resetPassword(u.user_id, null, true);
      toast.success(
        r.email_sent
          ? `Password reset. Email sent. Temp password: ${r.temp_password}`
          : `Password reset. Copy this to share manually: ${r.temp_password}`,
        { duration: 15000 }
      );
    } catch (e) {
      toast.error(e.message || 'Failed to reset password');
    }
  };

  const openNotifyDialog = (u) => {
    setNotifyTarget(u);
    setNotify({ subject: '', message: '' });
    setShowNotifyDialog(true);
  };

  const handleSendNotify = async () => {
    if (!notify.subject.trim() || !notify.message.trim()) {
      return toast.error('Subject and message are required');
    }
    setNotifying(true);
    try {
      const r = await usersApi.notify(notifyTarget.user_id, notify.subject.trim(), notify.message.trim());
      toast.success(r.email_sent ? 'Email sent' : 'Notification saved (email service unavailable)');
      setShowNotifyDialog(false);
    } catch (e) {
      toast.error(e.message || 'Failed to send');
    } finally {
      setNotifying(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersData, schoolsData, countsData] = await Promise.all([
        usersApi.getAll(),
        currentUser?.role === 'super_admin' ? schoolsApi.getAll() : Promise.resolve([]),
        parentLinksApi.counts().catch(() => ({ counts: {} })),
      ]);
      setUsers(usersData);
      setSchools(schoolsData);
      setParentLinkCounts(countsData.counts || {});
      setSelectedUserIds([]);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    setSavingUser(true);
    try {
      // Phase 40.35 — SuperAdmin can now change class/board/school and
      // relink parent from a single dialog.
      await usersApi.updateRole(selectedUser.user_id, {
        role: roleData.role,
        school_id: roleData.school_id || null,
      });
      // Update grade/board via PUT /users/{id}
      const profilePatch = {};
      if (roleData.grade !== '') profilePatch.grade = parseInt(roleData.grade);
      if ((roleData.board ?? '') !== '') profilePatch.board = roleData.board || null;
      if (roleData.school_id !== undefined) profilePatch.school_id = roleData.school_id || null;
      if (Object.keys(profilePatch).length > 0) {
        try { await usersApi.update(selectedUser.user_id, profilePatch); } catch { /* ignore secondary */ }
      }
      toast.success('User updated');
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingUser(false);
    }
  };

  const openRoleDialog = async (u) => {
    setSelectedUser(u);
    setRoleData({
      role: u.role,
      school_id: u.school_id || '',
      grade: u.grade ?? u.class_level ?? '',
      board: u.board || '',
    });
    setLinkedParent(null);
    setParentSearch('');
    setParentSearchResults([]);
    setShowDialog(true);
    // Fetch the currently-linked parent (if any) for student users.
    if (['org_student', 'independent_student'].includes(u.role)) {
      try {
        const r = await parentLinksApi.list({ childId: u.user_id });
        const first = (r.links || [])[0];
        if (first) {
          const parentUser = users.find(x => x.user_id === first.parent_id);
          if (parentUser) setLinkedParent(parentUser);
        }
      } catch { /* no link */ }
    }
  };

  const searchParents = (q) => {
    setParentSearch(q);
    if (q.trim().length < 2) { setParentSearchResults([]); return; }
    const term = q.toLowerCase();
    const matches = users
      .filter(u => u.role === 'parent')
      .filter(u => (u.name || '').toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term))
      .slice(0, 10);
    setParentSearchResults(matches);
  };

  const linkParentInline = async (parent) => {
    try {
      // If a parent is already linked, unlink first.
      if (linkedParent) {
        await parentLinksApi.remove({ parentId: linkedParent.user_id, childId: selectedUser.user_id });
      }
      await parentLinksApi.create({ parentId: parent.user_id, childId: selectedUser.user_id });
      setLinkedParent(parent);
      setParentSearch('');
      setParentSearchResults([]);
      toast.success(`Linked ${parent.name} as parent`);
      refreshParentLinkCounts();
    } catch (e) {
      toast.error(e.message || 'Failed to link parent');
    }
  };

  const unlinkParentInline = async () => {
    if (!linkedParent) return;
    try {
      await parentLinksApi.remove({ parentId: linkedParent.user_id, childId: selectedUser.user_id });
      setLinkedParent(null);
      toast.success('Parent unlinked');
      refreshParentLinkCounts();
    } catch (e) {
      toast.error(e.message || 'Failed to unlink');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    const selectableUsers = users.filter(u => u.role !== 'super_admin' && u.user_id !== currentUser?.user_id);
    if (selectedUserIds.length === selectableUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(selectableUsers.map(u => u.user_id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedUserIds.length} users? This action cannot be undone.`)) return;
    
    setDeleting(true);
    try {
      const result = await adminApi.bulkDeleteUsers(selectedUserIds);
      toast.success(result.message);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  const roleLabels = {
    super_admin: 'Super Admin',
    org_admin: 'School Admin',
    faculty: 'Faculty',
    org_student: 'School Student',
    independent_student: 'Independent'
  };

  const roleBadgeColors = {
    super_admin: 'bg-red-500',
    org_admin: 'bg-purple-500',
    faculty: 'bg-blue-500',
    org_student: 'bg-green-500',
    independent_student: 'bg-gray-500',
    parent: 'bg-amber-500',
  };

  const selectableUsers = users.filter(u => u.role !== 'super_admin' && u.user_id !== currentUser?.user_id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Users Management</h1>
        <div className="flex items-center gap-2">
          {(isSuper || isSchoolAdmin) && (
            <Button onClick={() => setShowAddDialog(true)} data-testid="add-user-btn">
              <UserPlus className="w-4 h-4 mr-2" />Add User
            </Button>
          )}
          {currentUser?.role === 'super_admin' && selectedUserIds.length > 0 && (
            <Button 
              variant="destructive" 
              onClick={handleBulkDelete}
              disabled={deleting}
              data-testid="bulk-delete-users-btn"
            >
              {deleting ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Delete {selectedUserIds.length} Users</>
              )}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="skeleton h-16 rounded-lg" />)}
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                {currentUser?.role === 'super_admin' && (
                  <th className="w-12">
                    <input 
                      type="checkbox" 
                      checked={selectedUserIds.length === selectableUsers.length && selectableUsers.length > 0}
                      onChange={toggleAllUsers}
                      className="rounded border-border"
                      data-testid="select-all-users"
                    />
                  </th>
                )}
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>School</th>
                <th>Links</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isSelectable = user.role !== 'super_admin' && user.user_id !== currentUser?.user_id;
                return (
                  <tr key={user.user_id} data-testid={`user-row-${user.user_id}`} className={selectedUserIds.includes(user.user_id) ? 'bg-primary/5' : ''}>
                    {currentUser?.role === 'super_admin' && (
                      <td>
                        {isSelectable ? (
                          <input 
                            type="checkbox" 
                            checked={selectedUserIds.includes(user.user_id)}
                            onChange={() => toggleUserSelection(user.user_id)}
                            className="rounded border-border"
                            data-testid={`select-user-checkbox-${user.user_id}`}
                          />
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </td>
                    )}
                    <td className="font-medium">{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`px-2 py-1 rounded text-xs text-white ${roleBadgeColors[user.role]}`}>
                        {roleLabels[user.role]}
                      </span>
                    </td>
                    <td>{schools.find(s => s.school_id === user.school_id)?.name || '-'}</td>
                    <td data-testid={`links-cell-${user.user_id}`}>
                      {user.role === 'parent' ? (
                        (() => {
                          const n = parentLinkCounts[user.user_id] || 0;
                          return (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${
                                n > 0
                                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                                  : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/30'
                              }`}
                              title={n > 0 ? `${n} child${n === 1 ? '' : 'ren'} linked` : 'No children linked yet'}
                              data-testid={`link-count-${user.user_id}`}
                            >
                              <Users2 className="w-3 h-3" />
                              {n} {n === 1 ? 'child' : 'children'}
                            </span>
                          );
                        })()
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </td>
                    <td>
                      <Button variant="ghost" size="sm" onClick={() => openRoleDialog(user)} data-testid={`edit-user-${user.user_id}`} title="Edit role">
                        <Edit className="w-4 h-4" />
                      </Button>
                      {user.role === 'parent' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openChildrenDialog(user)}
                          data-testid={`manage-children-${user.user_id}`}
                          title="Manage linked children"
                        >
                          <Users2 className="w-4 h-4 text-amber-500" />
                        </Button>
                      )}
                      {canManage(user) && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleResetPassword(user)} data-testid={`reset-pw-${user.user_id}`} title="Reset password">
                            <KeyRound className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openNotifyDialog(user)} data-testid={`notify-${user.user_id}`} title="Send email">
                            <Mail className="w-4 h-4 text-emerald-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(user)} data-testid={`delete-user-${user.user_id}`} title="Delete user">
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
            <p className="text-sm text-muted-foreground">User: <span className="font-semibold text-foreground">{selectedUser?.name}</span> · {selectedUser?.email}</p>

            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={roleData.role} onValueChange={(v) => setRoleData({ ...roleData, role: v })}>
                <SelectTrigger data-testid="role-select-dialog"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {currentUser?.role === 'super_admin' && (
                    <>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                      <SelectItem value="org_admin">School Admin</SelectItem>
                    </>
                  )}
                  <SelectItem value="faculty">Faculty</SelectItem>
                  <SelectItem value="org_student">School Student</SelectItem>
                  <SelectItem value="independent_student">Independent Student</SelectItem>
                  <SelectItem value="parent" data-testid="role-option-parent">Parent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* School — for org_admin / faculty / org_student */}
            {['org_admin', 'faculty', 'org_student'].includes(roleData.role) && (
              <div className="space-y-1.5">
                <Label>School</Label>
                {schools.length > 0 ? (
                  <Select value={roleData.school_id} onValueChange={(v) => setRoleData({ ...roleData, school_id: v })}>
                    <SelectTrigger data-testid="school-select-dialog">
                      <SelectValue placeholder="Select school" />
                    </SelectTrigger>
                    <SelectContent>
                      {schools.map((school) => (
                        <SelectItem key={school.school_id} value={school.school_id}>{school.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder="school_id (dev DB has no schools registered yet)"
                    value={roleData.school_id}
                    onChange={(e) => setRoleData({ ...roleData, school_id: e.target.value })}
                    data-testid="school-input-dialog"
                  />
                )}
              </div>
            )}

            {/* Phase 40.35 — Grade & Board for students */}
            {['org_student', 'independent_student'].includes(roleData.role) && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Class / Grade</Label>
                  <Select value={String(roleData.grade || '')} onValueChange={(v) => setRoleData({ ...roleData, grade: v })}>
                    <SelectTrigger data-testid="grade-select-dialog"><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>
                      {[5,6,7,8,9,10,11,12].map(g => <SelectItem key={g} value={String(g)}>Class {g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Board</Label>
                  <Select value={roleData.board || ''} onValueChange={(v) => setRoleData({ ...roleData, board: v })}>
                    <SelectTrigger data-testid="board-select-dialog"><SelectValue placeholder="Select board" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CBSE">CBSE</SelectItem>
                      <SelectItem value="ICSE">ICSE</SelectItem>
                      <SelectItem value="Maharashtra SSC">Maharashtra SSC</SelectItem>
                      <SelectItem value="Maharashtra HSC">Maharashtra HSC</SelectItem>
                      <SelectItem value="State Board">Other State Board</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Phase 40.35 — Link Parent (only for student roles) */}
            {['org_student', 'independent_student'].includes(roleData.role) && (
              <div className="space-y-1.5 border-t pt-3">
                <Label className="flex items-center gap-1.5"><Users2 className="w-4 h-4 text-amber-500" />Linked Parent</Label>
                {linkedParent ? (
                  <div className="flex items-center justify-between p-2.5 rounded-md border bg-muted/40" data-testid="linked-parent-row">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{linkedParent.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{linkedParent.email}</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={unlinkParentInline} data-testid="unlink-parent-btn">
                      <X className="w-4 h-4 text-rose-500" />
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No parent linked yet.</p>
                )}
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search parents by name or email…"
                    value={parentSearch}
                    onChange={(e) => searchParents(e.target.value)}
                    className="pl-9"
                    data-testid="parent-search-input"
                  />
                </div>
                {parentSearch.trim().length >= 2 && (
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {parentSearchResults.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic px-2 py-1">No matching parents.</p>
                    ) : (
                      parentSearchResults.map(p => (
                        <button
                          key={p.user_id}
                          type="button"
                          onClick={() => linkParentInline(p)}
                          className="w-full flex items-center justify-between p-2 rounded-md border hover:bg-muted/50 transition text-left"
                          data-testid={`parent-candidate-${p.user_id}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{p.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                          </div>
                          <UserPlus className="w-4 h-4 text-emerald-500 shrink-0" />
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateRole} disabled={savingUser} data-testid="update-role-btn">
              {savingUser ? <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />Saving…</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 38.5 — Manage Children dialog (admin attaches kids to a parent) */}
      <Dialog open={showChildrenDialog} onOpenChange={setShowChildrenDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid="manage-children-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users2 className="w-5 h-5 text-amber-500" />
              Children linked to {parentForChildren?.name || 'parent'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Currently linked */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Linked children · {linkedChildren.length}
              </Label>
              {linkedChildren.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground italic">No children linked yet.</p>
              ) : (
                <div className="mt-2 space-y-2">
                  {linkedChildren.map(c => (
                    <div
                      key={c.child_id}
                      className="flex items-center justify-between p-2.5 rounded-md border border-border bg-card"
                      data-testid={`linked-child-${c.child_id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{c.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {c.email}{c.grade ? ` · Class ${c.grade}` : ''}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => unlinkChild(c.child_id, c.name)}
                        data-testid={`unlink-child-${c.child_id}`}
                      >
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add new */}
            <div className="border-t border-border pt-4">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Link a new child
              </Label>
              <div className="mt-2 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search students by name or email…"
                  value={childSearch}
                  onChange={(e) => searchStudents(e.target.value)}
                  className="pl-9"
                  data-testid="child-search-input"
                />
              </div>
              {childSearch.trim().length >= 2 && (
                <div className="mt-2 max-h-56 overflow-y-auto space-y-1">
                  {childSearchResults.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic px-2 py-1">No matching students.</p>
                  ) : (
                    childSearchResults.map(s => (
                      <button
                        key={s.user_id}
                        onClick={() => linkChild(s)}
                        disabled={linkingChild}
                        className="w-full flex items-center justify-between p-2.5 rounded-md border border-border hover:bg-muted/50 transition text-left"
                        data-testid={`link-candidate-${s.user_id}`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{s.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {s.email}{s.grade ? ` · Class ${s.grade}` : ''}
                          </p>
                        </div>
                        <UserPlus className="w-4 h-4 text-emerald-500 shrink-0" />
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChildrenDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Phase 40.33 — Add User dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="add-user-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" />Add User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Full Name</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {isSuper && <SelectItem value="org_admin">School Admin</SelectItem>}
                  <SelectItem value="faculty">Faculty</SelectItem>
                  <SelectItem value="org_student">School Student</SelectItem>
                  {isSuper && <SelectItem value="independent_student">Independent Student</SelectItem>}
                  <SelectItem value="parent">Parent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isSuper && ['org_admin', 'faculty', 'org_student'].includes(newUser.role) && schools.length > 0 && (
              <div className="space-y-1.5">
                <Label>School</Label>
                <Select value={newUser.school_id} onValueChange={(v) => setNewUser({ ...newUser, school_id: v })}>
                  <SelectTrigger data-testid="new-user-school"><SelectValue placeholder="Select school" /></SelectTrigger>
                  <SelectContent>
                    {schools.map(s => <SelectItem key={s.school_id} value={s.school_id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {['org_student', 'independent_student'].includes(newUser.role) && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label>Grade / Class</Label>
                  <Input type="number" min="1" max="12" value={newUser.grade} onChange={(e) => setNewUser({ ...newUser, grade: e.target.value })} data-testid="new-user-grade" />
                </div>
                <div className="space-y-1.5">
                  <Label>Board</Label>
                  <Input placeholder="CBSE / ICSE / …" value={newUser.board} onChange={(e) => setNewUser({ ...newUser, board: e.target.value })} data-testid="new-user-board" />
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground pt-1">
              A random password will be generated and emailed to the user.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} disabled={creating} data-testid="create-user-btn">
              {creating ? <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />Creating…</> : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 40.33 — Notify dialog */}
      <Dialog open={showNotifyDialog} onOpenChange={setShowNotifyDialog}>
        <DialogContent data-testid="notify-user-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-emerald-500" />Send email to {notifyTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">To: <span className="font-mono">{notifyTarget?.email}</span></p>
            <div className="space-y-1.5">
              <Label>Subject</Label>
              <Input value={notify.subject} onChange={(e) => setNotify({ ...notify, subject: e.target.value })} data-testid="notify-subject" />
            </div>
            <div className="space-y-1.5">
              <Label>Message</Label>
              <Textarea rows={6} value={notify.message} onChange={(e) => setNotify({ ...notify, message: e.target.value })} data-testid="notify-message" placeholder="Write a message that will be emailed to this user…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotifyDialog(false)}>Cancel</Button>
            <Button onClick={handleSendNotify} disabled={notifying} data-testid="send-notify-btn">
              {notifying ? <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />Sending…</> : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

